import { useEffect, useRef } from "react";
import CustomerCarousel from "./CustomerCarousel.tsx";
import { heroSection } from "./data.tsx";
import { ArrowUpRightIcon } from "@phosphor-icons/react/dist/ssr/ArrowUpRight";
import { CheckCircle } from "@phosphor-icons/react/dist/ssr/CheckCircle";
import HeroVideo from "../../assets/HomeVideo.mp4";

const featureChips = [
  "AI Video Interviews",
  "AI Phone Screening",
  "Code Assessments",
  "Aptitude Tests",
  "Personality Tests",
  "WhatsApp",
];

const HeroPage = () => {
  const { title, subtitle, cta1, cta2 } = heroSection;
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedElements =
      sectionRef.current?.querySelectorAll(".animate-on-scroll");
    animatedElements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative flex w-screen snap-start snap-always flex-col items-center justify-start overflow-hidden bg-gray-900 px-4 pb-8 pt-20 lg:pb-16 lg:pt-28"
    >
      {/* Background gradient effects */}
      <div className="pointer-events-none absolute inset-0">
        <div className="bg-app-500/8 absolute left-1/4 top-0 h-[600px] w-[600px] -translate-y-1/3 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 h-[400px] w-[400px] translate-x-1/4 rounded-full bg-app-500/5 blur-[100px]" />
      </div>

      {/* Two-column hero layout */}
      <div className="relative z-10 mx-auto flex w-full max-w-screen-xl flex-col items-center gap-10 lg:flex-row lg:items-center lg:justify-between lg:gap-12">
        {/* Left column - Text content */}
        <div className="flex flex-col items-center text-center lg:w-[48%] lg:shrink-0 lg:items-start lg:text-left">
          {/* Badge */}

          {/* Headline */}
          <h1 className="animate-on-scroll fade-up delay-1 font-serif text-3xl font-medium leading-tight tracking-tight md:text-4xl lg:text-5xl">
            <span className="text-[#D0F961]">
              Humans are still the best judge.
            </span>
            <span className="font-light italic text-white">
              {" "}
              AI helps find the best hires, faster.
            </span>
          </h1>

          {/* Subtitle */}
          <p className="animate-on-scroll fade-up delay-2 mt-5 max-w-lg font-serif text-base leading-relaxed text-gray-300 md:text-lg">
            {subtitle}
          </p>

          {/* Description */}
          {/* <p className="animate-on-scroll fade-up delay-2 mt-2 font-sans text-sm tracking-wide text-gray-500">
            {description}
          </p> */}

          {/* CTA Buttons */}
          <div className="animate-on-scroll fade-up delay-3 mt-8 flex flex-col items-center gap-4 sm:flex-row lg:items-start">
            <a
              href={cta1.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex items-center gap-2 overflow-hidden rounded-full bg-app-300 px-6 py-3 text-base font-semibold text-gray-900 transition-all duration-300 hover:bg-app-200 hover:shadow-[0_0_30px_rgba(99,205,220,0.4)]"
            >
              <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
              {cta1.text}
              <ArrowUpRightIcon
                size={18}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>

            <a
              href={cta2.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-base font-medium text-white transition-all duration-300 hover:gap-3 hover:border-white/60 hover:bg-white/10 hover:shadow-[0_0_20px_rgba(255,255,255,0.08)]"
            >
              {cta2.text}
              <ArrowUpRightIcon
                size={18}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </div>

          {/* Feature chips below CTA */}
          <div className="animate-on-scroll fade-up delay-4 mt-6 flex flex-wrap items-center justify-center gap-2.5 lg:justify-start">
            {featureChips.map((chip) => (
              <span
                key={chip}
                className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-gray-300"
              >
                <CheckCircle size={14} weight="fill" className="text-app-400" />
                {chip}
              </span>
            ))}
          </div>
        </div>

        {/* Right column - Hero Image */}
        <div className="animate-on-scroll fade-up delay-3 flex items-center justify-center lg:w-[52%] lg:justify-end">
          <div className="relative">
            <video
              src={HeroVideo}
              autoPlay
              loop
              muted
              playsInline
              className="w-full rounded-2xl shadow-2xl shadow-app-500/10 lg:max-w-none"
            />
            {/* Glow effect under the image */}
            <div className="absolute -bottom-4 left-1/2 h-10 w-3/4 -translate-x-1/2 rounded-full bg-app-500/15 blur-2xl" />
          </div>
        </div>
      </div>

      {/* Customer logos carousel */}
      <div className="slider-container relative z-10 mx-auto mt-12 w-full max-w-screen-xl overflow-hidden md:mt-16">
        <p className="mb-4 text-center text-xs font-medium uppercase tracking-[0.2em] text-gray-500">
          Trusted by leading companies
        </p>
        <CustomerCarousel />
      </div>

      {/* CSS for animations */}
      <style>{`
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.7s ease-out, transform 0.7s ease-out;
        }
        .animate-on-scroll.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
        .delay-1 { transition-delay: 0.1s; }
        .delay-2 { transition-delay: 0.2s; }
        .delay-3 { transition-delay: 0.3s; }
        .delay-4 { transition-delay: 0.4s; }
      `}</style>
    </section>
  );
};

export default HeroPage;
